import type { StatutCommande } from './types'

/**
 * Annulation d'une commande par le dispatch.
 *
 * Les règles vivent dans l'edge function `set-order-status` ; ce module ne les
 * rejoue pas, il les REFLÈTE pour ne proposer le bouton qu'aux statuts où la
 * transition est permise au dispatch, et pour annoncer le bon montant.
 * Si la table TRANSITIONS de la fonction change, `annulation.test.ts` la relit et
 * échoue : les deux côtés ne peuvent pas diverger en silence.
 */

/** Statuts depuis lesquels `cancelled` est permis au rôle « dispatch ». */
export const STATUTS_ANNULABLES: readonly StatutCommande[] = [
  'pending',
  'paid',
  'accepted',
  'preparing',
  'ready',
  'rider_assigned',
]

export const peutAnnuler = (statut: StatutCommande): boolean => STATUTS_ANNULABLES.includes(statut)

/** Pourquoi le bouton est absent — pour le dire plutôt que de laisser chercher. */
export function raisonNonAnnulable(statut: StatutCommande): string | null {
  if (peutAnnuler(statut)) return null
  if (statut === 'cancelled') return 'Cette commande est déjà annulée.'
  if (statut === 'delivered') return 'Cette commande est livrée : elle ne peut plus être annulée.'
  return "Le livreur a déjà récupéré la commande : elle ne peut plus être annulée d'ici. Contactez le livreur."
}

export const MOTIF_MIN = 5
export const MOTIF_MAX = 300

export type ResultatMotif = { ok: true; motif: string } | { ok: false; message: string }

/** Le motif est OBLIGATOIRE : il part dans l'historique, vers le marchand et le client. */
export function validerMotif(brut: string): ResultatMotif {
  const motif = brut.replace(/\s+/g, ' ').trim()
  if (motif.length === 0) return { ok: false, message: "Indiquez le motif de l'annulation." }
  if (motif.length < MOTIF_MIN) {
    return { ok: false, message: `Motif trop court (au moins ${MOTIF_MIN} caractères) : dites pourquoi.` }
  }
  if (motif.length > MOTIF_MAX) {
    return { ok: false, message: `Motif trop long (${MOTIF_MAX} caractères au maximum).` }
  }
  return { ok: true, motif }
}

/**
 * Montant à rembourser au client, tel que le serveur le calcule : une annulation
 * par le dispatch rembourse le TOTAL. Une commande encore `pending` n'a jamais été
 * payée — il n'y a rien à rendre (le serveur inscrit quand même `refund_amount`,
 * mais aucun paiement abouti n'existe derrière).
 */
export function montantRemboursable(statut: StatutCommande, total: number): number {
  if (statut === 'pending') return 0
  return Math.max(0, Math.round(Number(total) || 0))
}
